/**
 * Authentication Context and API Service
 * Handles all authentication state and API calls
 * 
 * IMPORTANT: Tokens are stored in HttpOnly cookies - NOT accessible via JavaScript
 * This provides XSS protection - even if malicious script runs, it cannot steal tokens
 * 
 * Features:
 * - isInitializing state to prevent premature redirects
 * - Refresh locking to prevent duplicate refresh calls
 * - Race condition prevention with promise-based waiting
 * - Comprehensive logging for debugging
 * - Automatic refresh on browser restart
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../shared/api/config';

const API_URL = API_BASE;

// Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isInitializing, setIsInitializing] = useState(true);
    const [error, setError] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // =========================================================================
    // REFRESH LOCKING MECHANISM
    // =========================================================================
    const refreshLockRef = useRef(false);
    const refreshPromiseRef = useRef(null);
    const refreshInProgressRef = useRef(false);

    /**
     * Check if refresh is currently in progress
     */
    const isRefreshInProgress = useCallback(() => {
        return refreshLockRef.current;
    }, []);

    /**
     * Wait for any ongoing refresh to complete
     * Returns a promise that resolves when refresh is done
     */
    const waitForRefresh = useCallback(() => {
        if (refreshPromiseRef.current) {
            console.log('[Auth] Refresh already in progress, waiting for it to complete...');
            return refreshPromiseRef.current;
        }
        return Promise.resolve();
    }, []);

    // =========================================================================
    // REFRESH TOKEN FUNCTION WITH LOCKING
    // =========================================================================
    
    // Refresh access token using refresh token
    // This is called automatically when access token expires
    const refreshAccessToken = useCallback(async (source = 'unknown') => {
        console.log(`[Auth] Attempting to refresh access token from source: ${source}`);
        
        // Check if refresh is already in progress
        if (refreshLockRef.current) {
            console.log(`[Auth] DUPLICATE PREVENTED: Refresh attempt from ${source} blocked by lock`);
            // Wait for the ongoing refresh to complete
            try {
                await waitForRefresh();
                console.log('[Auth] Waiting for refresh completed, proceeding with cached result');
                return true;
            } catch (err) {
                console.error('[Auth] Wait for refresh failed:', err);
                return false;
            }
        }

        // Acquire lock
        refreshLockRef.current = true;
        refreshInProgressRef.current = true;

        // Create a promise that others can wait on
        let resolveRefresh;
        let rejectRefresh;
        refreshPromiseRef.current = new Promise((resolve, reject) => {
            resolveRefresh = resolve;
            rejectRefresh = reject;
        });

        try {
            // Check if refresh token cookie exists before making request
            const response = await fetch(`${API_URL}/api/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',  // Send refresh token cookie automatically
            });

            if (response.ok) {
                console.log('[Auth] Token refreshed successfully');
                resolveRefresh(true);
                return true;
            } else if (response.status === 401) {
                const data = await response.json().catch(() => ({}));
                
                // Log specific reason for 401
                if (data.code === 'NO_REFRESH_TOKEN') {
                    console.warn('[Auth] COOKIE MISSING: refresh_token cookie not found');
                } else if (data.code === 'TOKEN_REVOKED') {
                    console.log('[Auth] Refresh token was revoked');
                } else if (data.code === 'INVALID_REFRESH_TOKEN') {
                    console.log('[Auth] Refresh token is invalid or expired');
                }
                resolveRefresh(false);
                return false;
            } else {
                console.log('[Auth] Token refresh failed with status:', response.status);
                resolveRefresh(false);
                return false;
            }
        } catch (err) {
            console.error('[Auth] Token refresh error:', err);
            rejectRefresh(err);
            return false;
        } finally {
            // Release lock
            refreshLockRef.current = false;
            refreshInProgressRef.current = false;
            refreshPromiseRef.current = null;
        }
    }, [waitForRefresh]);

    // =========================================================================
    // CHECK AUTH FUNCTION - FIXED FOR RACE CONDITIONS
    // =========================================================================

    // Check if user is authenticated
    const checkAuth = useCallback(async (retryCount = 0) => {
        console.log('[Auth] checkAuth called - checking HttpOnly cookies');
        
        // Don't attempt refresh if another refresh is in progress
        if (isRefreshInProgress()) {
            console.log('[Auth] REFRESH SKIPPED: Refresh already in progress');
            // Wait for the ongoing refresh to complete
            await waitForRefresh();
            // After waiting, check if we're now authenticated
            // Return current state without changing loading
            return isAuthenticated;
        }

        try {
            // Call /api/auth/me - cookies are sent automatically with credentials: 'include'
            const response = await fetch(`${API_URL}/api/auth/me`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',  // IMPORTANT: Send cookies with request
            });

            console.log('[Auth] /api/auth/me response status:', response.status);

            if (response.ok) {
                const data = await response.json();
                console.log('[Auth] /api/auth/me success, user:', data.user);
                
                // Update state only if not already authenticated
                if (!isAuthenticated) {
                    setUser(data.user);
                    setIsAuthenticated(true);
                }
                
                // Only set loading to false after initial check is complete
                setLoading(false);
                setIsInitializing(false);
                return true;
            } else if (response.status === 401) {
                // Token expired - try to refresh token (max 1 retry)
                if (retryCount === 0) {
                    console.log('[Auth] Token expired, attempting refresh...');
                    const refreshed = await refreshAccessToken('checkAuth');
                    
                    if (refreshed) {
                        // Token refreshed successfully, retry checkAuth
                        console.log('[Auth] Token refreshed, retrying checkAuth...');
                        return checkAuth(retryCount + 1);
                    }
                }
                
                // Refresh failed or already retried - user not authenticated
                console.log('[Auth] Not authenticated after refresh attempt');
                
                // Only set to false if we're done initializing
                setUser(null);
                setIsAuthenticated(false);
                setLoading(false);
                setIsInitializing(false);
                return false;
            } else {
                // Other errors - keep user logged in but log the error
                console.error('[Auth] Auth check failed with status:', response.status);
                setIsAuthenticated(true);
                setLoading(false);
                setIsInitializing(false);
                return true;
            }
        } catch (err) {
            console.error('[Auth] Auth check failed:', err);
            // Network error - assume user is not authenticated
            setUser(null);
            setIsAuthenticated(false);
            setLoading(false);
            setIsInitializing(false);
            return false;
        }
    }, [refreshAccessToken, isRefreshInProgress, waitForRefresh, isAuthenticated]);

    // =========================================================================
    // INITIAL AUTH CHECK ON MOUNT
    // =========================================================================

    // Check authentication on mount
    useEffect(() => {
        console.log('[Auth] Initial checkAuth - tokens in HttpOnly cookies');
        
        // Call checkAuth which will handle refresh if needed
        // This ensures we attempt refresh before any login redirect
        checkAuth();
    }, []); // Empty deps - run once on mount

    // =========================================================================
    // PERIODIC TOKEN REFRESH WITH LOCKING
    // =========================================================================

    // Periodic token refresh to keep user logged in
    // Refreshes every 7 hours (before the 8-hour access token expiry)
    useEffect(() => {
        if (!isAuthenticated || isInitializing) return;

        console.log('[Auth] Setting up periodic token refresh');
        
        // Refresh token every 7 hours (25200000 ms)
        const REFRESH_INTERVAL = 7 * 60 * 60 * 1000; // 7 hours in milliseconds
        
        const intervalId = setInterval(async () => {
            console.log('[Auth] Periodic token refresh triggered');
            
            // Check if refresh is already in progress
            if (isRefreshInProgress()) {
                console.log('[Auth] DUPLICATE PREVENTED: periodicTimer');
                return;
            }
            
            const success = await refreshAccessToken('periodicTimer');
            if (success) {
                console.log('[Auth] Periodic token refresh successful');
            } else {
                console.log('[Auth] Periodic token refresh failed - may need to login again');
            }
        }, REFRESH_INTERVAL);

        return () => {
            console.log('[Auth] Clearing periodic token refresh');
            clearInterval(intervalId);
        };
    }, [isAuthenticated, isInitializing, refreshAccessToken, isRefreshInProgress]);

    // =========================================================================
    // LOGIN FUNCTIONS
    // =========================================================================

    // Login with email and password
    const login = async (email, password) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
                credentials: 'include',  // Send cookies
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Login failed');
            }

            // Store user_id for OTP verification step
            sessionStorage.setItem('pending_user_id', data.user_id);
            
            return {
                step: 'otp',
                user_id: data.user_id,
            };
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Verify OTP and complete login
    // NOTE: Tokens are set in HttpOnly cookies by the server - not accessible to JS
    const verifyOtp = async (userId, otp) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: userId, otp }),
                credentials: 'include',  // IMPORTANT: HttpOnly cookies will be set by server
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'OTP verification failed');
            }

            console.log('[Auth] OTP verified, tokens set in HttpOnly cookies');
            
            // Clear pending user
            sessionStorage.removeItem('pending_user_id');
            
            // Set user - tokens are in cookies, not accessible to JS
            setUser(data.user);
            setIsAuthenticated(true);
            setLoading(false);
            setIsInitializing(false);
            
            console.log('[Auth] User logged in:', data.user);
            
            return data;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Resend OTP
    const resendOtp = async (userId) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/auth/resend-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: userId }),
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to resend OTP');
            }

            return data;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Set password (first time or reset)
    const setPassword = async (token, password) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/auth/set-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, password }),
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to set password');
            }

            return data;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // =========================================================================
    // LOGOUT FUNCTION
    // =========================================================================

    // Logout
    // Clears HttpOnly cookies and invalidates session on server
    const logout = async () => {
        console.log('[Auth] Logging out...');
        try {
            await fetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',  // Send cookies so server can clear them
            });
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            // Clear local state - tokens are in cookies (HttpOnly) but will be cleared
            setUser(null);
            setIsAuthenticated(false);
            setLoading(false);
            setIsInitializing(false);
            console.log('[Auth] Logged out successfully');
        }
    };

    // =========================================================================
    // HELPER FUNCTIONS
    // =========================================================================

    // Get current user info
    const getUser = useCallback(() => user, [user]);

    // Check if user has specific role(s)
    // @param {string|string[]} roles - Single role string or array of roles
    // @returns {boolean} - True if user has any of the specified roles
    const hasRole = useCallback((roles) => {
        if (!user || !user.role) return false;
        
        // Handle single role string
        if (typeof roles === 'string') {
            return user.role === roles;
        }
        
        // Handle array of roles
        if (Array.isArray(roles)) {
            return roles.includes(user.role);
        }
        
        return false;
    }, [user]);

    // Check if user is superadmin
    // @returns {boolean} - True if user has SuperAdmin role
    const isSuperAdmin = useCallback(() => {
        return user?.role === 'SuperAdmin';
    }, [user]);

    // Check if user is staff (only Staff role, NOT SuperAdmin)
    // @returns {boolean} - True only if user has Staff role exclusively
    // IMPORTANT: This returns false for SuperAdmin users
    const isStaff = useCallback(() => {
        return user?.role === 'Staff';
    }, [user]);

    const value = {
        user,
        loading,
        isInitializing,
        error,
        isAuthenticated,
        login,
        verifyOtp,
        resendOtp,
        setPassword,
        logout,
        getUser,
        hasRole,
        isSuperAdmin,
        isStaff,
        checkAuth,
        refreshAccessToken,
        isRefreshInProgress,
        API_URL,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// Custom hook to use auth context
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

/**
 * Custom hook for easy role-based permission checks
 * 
 * Usage:
 * const { canAccess, isSuperAdmin, isStaff } = usePermission();
 * 
 * // Check specific roles
 * if (canAccess(['SuperAdmin'])) { ... }
 * if (canAccess(['Staff', 'SuperAdmin'])) { ... }
 * 
 * @returns {Object} - Permission helper functions
 */
export function usePermission() {
    const { hasRole, isSuperAdmin, isStaff, user } = useAuth();
    
    return {
        /**
         * Check if current user can access routes with given roles
         * @param {string|string[]} roles - Role or array of roles to check
         * @returns {boolean}
         */
        canAccess: hasRole,
        
        /**
         * Check if current user is SuperAdmin
         * @returns {boolean}
         */
        isSuperAdmin,
        
        /**
         * Check if current user is Staff (excludes SuperAdmin)
         * @returns {boolean}
         */
        isStaff,
        
        /**
         * Get current user role
         * @returns {string|null}
         */
        role: user?.role || null,
        
        /**
         * Check if user is authenticated
         * @returns {boolean}
         */
        isAuthenticated: !!user
    };
}

// =========================================================================
// API HELPER WITH AUTOMATIC TOKEN REFRESH
// =========================================================================

/**
 * API helper with automatic cookie credentials and token refresh
 * 
 * IMPORTANT: This helper uses credentials: 'include' to send HttpOnly cookies
 * Tokens are NOT accessible via JavaScript - they are automatically sent by the browser
 * 
 * This helper also handles automatic token refresh when receiving 401 errors,
 * allowing users to stay logged in even after their access token expires.
 * 
 * Features:
 * - Only retries once after successful refresh (prevents infinite loops)
 * - Checks for active refresh before attempting new refresh
 * - Proper error handling and logging
 */
export async function authFetch(url, options = {}, retryCount = 0) {
    const MAX_RETRIES = 1; // Only retry once to prevent infinite loops
    
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };
    
    const response = await fetch(`${API_URL}${url}`, {
        ...options,
        headers,
        credentials: 'include',  // IMPORTANT: Send cookies automatically
    });
    
    // Handle 401 Unauthorized - Token invalid/expired
    if (response.status === 401) {
        // Try to refresh token (max 1 retry)
        if (retryCount < MAX_RETRIES) {
            console.log(`[Auth] authFetch received 401, attempting token refresh (retry ${retryCount + 1})...`);
            try {
                // First check if a refresh is already in progress by calling the refresh endpoint
                // This allows us to wait for an existing refresh rather than starting a new one
                const refreshResponse = await fetch(`${API_URL}/api/auth/refresh`, {
                    method: 'POST',
                    credentials: 'include',
                });
                
                if (refreshResponse.ok) {
                    console.log('[Auth] Token refreshed, retrying original request...');
                    // Retry the original request with new token
                    return authFetch(url, options, retryCount + 1);
                } else {
                    // Refresh failed - check why
                    const refreshData = await refreshResponse.json().catch(() => ({}));
                    console.error('[Auth] Token refresh failed:', refreshData.error || 'Unknown error');
                    
                    // Don't retry if refresh failed - throw session expired
                    throw new Error('Session expired. Please login again.');
                }
            } catch (refreshErr) {
                console.error('[Auth] Token refresh error:', refreshErr);
                throw new Error('Session expired. Please login again.');
            }
        }
        // Refresh failed or already retried max times - force logout for deleted/disabled accounts
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
        throw new Error('Session expired or account disabled.');
    }
    
    // Handle 403 Forbidden - Not enough permissions
    if (response.status === 403) {
        throw new Error('You do not have permission to access this resource.');
    }
    
    return response;
}

export default AuthContext;
